import { Guid, ReadonlyCollection, ValueOf } from "@ioip/core/lib";
import Attribute from "../data/types/Attribute";
import Entity from "../data/types/Entity";
import { Form } from "../forms/types";
import { IFormSchema } from "../forms/types/IFormSchema";
import { IObject } from "../portal/types";
import { NativeEntitySchemas } from "../types";
import { IExtendedProjectSchema } from "./IExtendedProjectSchema";

export interface IProjectProps {
    schema: IExtendedProjectSchema;
}

export class CProject {

    #props: IProjectProps;
    #entities: ReadonlyCollection<Entity>;
    #nativeEntities: ReadonlyCollection<Entity>;

    constructor(props: IProjectProps) {

        this.#props = props;
        this.#entities = new ReadonlyCollection(props.schema.entities.map(x => new Entity({ object: x, project: this })));
        this.#nativeEntities = new ReadonlyCollection(NativeEntitySchemas.map(x => new Entity({ object: x, project: this })));

    }

    get name(): string {
        return this.#props.schema.name;
    }

    get entities(): ReadonlyCollection<Entity> {
        return this.#entities;
    }

    get nativeEntities(): ReadonlyCollection<Entity> {
        return this.#nativeEntities;
    }

    get forms(): IObject<IFormSchema>[] {
        return this.#props.schema.forms;
    }

    findEntity(codeOrId: Guid | string): Entity {
        if (codeOrId instanceof Guid) {
            return this.entities.firstOrDefault(x => codeOrId.equals(x.objectId));
        }
        else {
            const guidValue = new ValueOf<Guid>();
            if (Guid.tryParse(codeOrId, guidValue)) {
                return this.entities.firstOrDefault(x => guidValue.value.equals(x.objectId));
            } else {
                return this.entities.firstOrDefault(x => x.code === codeOrId);
            }
        }
    }

    /**
     * Search attribute by ID or code in an entity
     * @param objectId ID or code of entity
     * @param attributeId ID or code of attribute
     * @returns 
     */
    findAttribute(objectId: Guid | string, attributeId: Guid | string): Attribute {

        // Obtener objeto entidad
        const entity = this.findEntity(objectId);
        if (entity == null)
            return null;


        const attributes = entity.attributes;
        if (attributeId instanceof Guid) {
            return attributes.firstOrDefault(x => attributeId.equals(x.id));
        }
        else {
            const guidValue = new ValueOf<Guid>();
            if (Guid.tryParse(attributeId, guidValue)) {
                return attributes.firstOrDefault(x => guidValue.value.equals(x.id));
            } else {
                return attributes.firstOrDefault(x => x.code === attributeId);
            }
        }
    }

    findForm(codeOrId: string): IObject<IFormSchema> {
        return this.forms.find(x => x.objectId == codeOrId);
    }

    getForm(codeOrId: Guid | string): Form {
        const schema = codeOrId instanceof Guid ? this.#props.schema.forms.find(x => codeOrId.equals(x.objectId)) : this.#props.schema.forms.find(x => x.code == codeOrId);
        if (schema == null)
            throw new Error(`Form ${codeOrId} not found.`);
        return new Form({ object: JSON.parse(JSON.stringify(schema)) });
    }

    getSchema(): IExtendedProjectSchema {
        return this.#props.schema;
    }

    getAllEntities(): Entity[] {
        return [ ...this.#entities.orderBy(x => x.name).toArray(), ...this.#nativeEntities.orderBy(x => x.name).toArray() ];
    }

}